/**
 * Deck card slot identity & merge rule (single source of truth).
 *
 * Invariant: within a deck, a given card edition (`scryfall_id`) has **at most
 * one** card document per zone. Whenever a card is added to or moved into a zone
 * that already contains the same edition, the quantities are merged into the
 * existing slot instead of creating a duplicate document.
 *
 * Keep all logic that enforces this invariant in this module so every add/move
 * path (single, bulk, import) stays consistent.
 */
import type { DeckZone } from '@/types/deck';

/** Minimal shape needed to reason about a deck card slot. */
export interface DeckCardSlot {
  id: string;
  scryfall_id: string;
  zone: DeckZone;
  quantity: number;
}

/** Stable identity key for a deck card slot: one slot per (scryfall_id, zone). */
export function deckCardSlotKey(scryfallId: string, zone: DeckZone): string {
  return `${zone}::${scryfallId}`;
}

/**
 * Find the existing slot for a card edition within a zone, if any.
 * Optionally exclude a specific card id (e.g. the card being moved).
 */
export function findDeckCardSlot<T extends Pick<DeckCardSlot, 'id' | 'scryfall_id' | 'zone'>>(
  cards: readonly T[],
  scryfallId: string,
  zone: DeckZone,
  excludeId?: string,
): T | undefined {
  return cards.find((c) => c.scryfall_id === scryfallId && c.zone === zone && c.id !== excludeId);
}

/** Plan for adding a quantity of a card edition into a zone. */
export type DeckCardAdditionPlan =
  | { type: 'increment'; cardId: string; newQuantity: number }
  | { type: 'create'; scryfallId: string; cardName: string | null; zone: DeckZone; quantity: number };

/**
 * Decide whether adding a card edition to a zone should increment an existing
 * slot or create a new one. Use this everywhere a card is added to a deck.
 */
export function planDeckCardAddition<T extends DeckCardSlot>(
  cards: readonly T[],
  addition: { scryfallId: string; cardName?: string | null; zone: DeckZone; quantity: number },
): DeckCardAdditionPlan {
  const existing = findDeckCardSlot(cards, addition.scryfallId, addition.zone);
  if (existing) {
    return { type: 'increment', cardId: existing.id, newQuantity: existing.quantity + addition.quantity };
  }
  return {
    type: 'create',
    scryfallId: addition.scryfallId,
    cardName: addition.cardName ?? null,
    zone: addition.zone,
    quantity: addition.quantity,
  };
}

/** Operations to apply for a set of zone moves, after merging colliding slots. */
export interface DeckCardMovePlan {
  /** Cards whose zone changes (no merge target). */
  zoneUpdates: { cardId: string; zone: DeckZone }[];
  /** Existing slots that absorb merged quantity (absolute new quantity). */
  quantityUpdates: { cardId: string; quantity: number }[];
  /** Source cards merged into another slot and removed. */
  deletes: string[];
}

/**
 * Compute the operations needed to move cards to new zones while preserving the
 * one-slot-per-(edition, zone) invariant. When a moved card lands in a zone that
 * already holds the same edition (an existing slot or another moved card), the
 * quantities are merged into a single slot and the redundant documents deleted.
 *
 * Total quantity is preserved, so deck `card_count` is unchanged by a move.
 */
export function planDeckCardMoves<T extends DeckCardSlot>(
  cards: readonly T[],
  moves: readonly { cardId: string; zone: DeckZone }[],
): DeckCardMovePlan {
  const byId = new Map(cards.map((c) => [c.id, c]));
  const moveMap = new Map<string, DeckZone>();
  for (const move of moves) {
    if (byId.has(move.cardId)) moveMap.set(move.cardId, move.zone);
  }

  const finalZone = (card: T): DeckZone => moveMap.get(card.id) ?? card.zone;

  // Group all cards by their final slot.
  const groups = new Map<string, T[]>();
  for (const card of cards) {
    const key = deckCardSlotKey(card.scryfall_id, finalZone(card));
    const group = groups.get(key);
    if (group) group.push(card);
    else groups.set(key, [card]);
  }

  const plan: DeckCardMovePlan = { zoneUpdates: [], quantityUpdates: [], deletes: [] };

  for (const group of groups.values()) {
    const movers = group.filter((c) => moveMap.has(c.id));
    if (movers.length === 0) continue; // nothing in this slot is changing

    // Prefer a stationary card as the surviving slot so its zone is untouched;
    // otherwise the first mover becomes the surviving slot.
    const winner = group.find((c) => !moveMap.has(c.id)) ?? movers[0];
    const totalQuantity = group.reduce((sum, c) => sum + c.quantity, 0);

    if (moveMap.has(winner.id) && byId.get(winner.id)!.zone !== finalZone(winner)) {
      plan.zoneUpdates.push({ cardId: winner.id, zone: finalZone(winner) });
    }
    if (totalQuantity !== winner.quantity) {
      plan.quantityUpdates.push({ cardId: winner.id, quantity: totalQuantity });
    }
    for (const card of group) {
      if (card.id !== winner.id) plan.deletes.push(card.id);
    }
  }

  return plan;
}
